import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Map, Set, OrderedMap, List } from 'immutable';
import VisibilitySensor from 'react-visibility-sensor';
import isNaN from 'lodash/isNaN';

import { routerLocationShape, dataCollectorShape, braintreeClientInstanceShape, paypalInstanceShape, applePayInstanceShape } from 'lib/common/prop-types';

import Quote from 'models/quote';
import { Location } from 'models/locations';
import Venue from 'models/venue';
import Search from 'models/search';
import { Fee } from 'models/fees';
import User from 'models/user';
import { AddOns as AddOnsModel } from 'models/addons';

import { AppConsumer } from 'providers/app-provider';

import ParkingDetails from 'containers/checkout/parking-details';
import AddOns from 'components/checkout/add-ons';
import AccountInformation from 'components/checkout/account-information';
import PaymentInformation from 'containers/checkout/payment-information';
import CheckoutSummary from 'components/common/checkout-summary';
import ProgressButton from 'components/common/buttons/progress-button';
import ApplePayButton from 'components/common/buttons/apple-pay-button';
import ZagSad from 'components/svgs/icons/zag-sad';
import TierAmenities from 'components/checkout/tier-amenities';
import MinimalBadges from 'components/checkout/checkout-form/minimal-badges';
import TermsOfService from 'components/checkout/checkout-form/terms-of-service';
import PurchaseDeniedMessage from 'components/checkout/checkout-form/purchase-denied-message';

import {
  TIME_IN_IMPRESSION,
  TIME_OUT_IMPRESSION,
  TIME_FORMAT,
} from 'lib/analytics/events';

const propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  locale: PropTypes.string.isRequired,
  requestQueueLength: PropTypes.number.isRequired,
  timezone: PropTypes.string.isRequired,
  user: PropTypes.instanceOf(User).isRequired,
  routerLocation: routerLocationShape.isRequired,
  userInfo: PropTypes.instanceOf(Map).isRequired,

  mounted: PropTypes.bool,
  selectedQuote: PropTypes.instanceOf(Quote),
  selectedLocation: PropTypes.instanceOf(Location),
  venue: PropTypes.instanceOf(Venue),
  apiError: PropTypes.instanceOf(Map),
  error: PropTypes.instanceOf(Map),
  errorButton: PropTypes.bool,
  fees: PropTypes.instanceOf(List).isRequired,
  requiredFields: PropTypes.instanceOf(Set),
  shouldScrollToError: PropTypes.bool.isRequired,
  checkoutTotal: PropTypes.number,
  subTotal: PropTypes.number,
  couponAmount: PropTypes.number,
  userCreditAmount: PropTypes.number,
  couponCode: PropTypes.string,
  selectedAddOns: PropTypes.instanceOf(AddOnsModel),
  paymentMethods: PropTypes.instanceOf(OrderedMap),
  isSubmitted: PropTypes.bool,
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(OrderedMap),
  ]),
  applePayInstance: applePayInstanceShape,
  dataCollector: dataCollectorShape,
  braintreeClientInstance: braintreeClientInstanceShape,
  paypalInstance: paypalInstanceShape,

  handleSubmit: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  setShouldScrollToError: PropTypes.func.isRequired,
  submitCheckout: PropTypes.func.isRequired,
  changeAddOns: PropTypes.func.isRequired,
  onLocationDetailsVisibilityChange: PropTypes.func.isRequired,
  saveSearch: PropTypes.func.isRequired,
  setUserInfo: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  onAccountVisibilityChange: PropTypes.func.isRequired,
  onUserInfoChange: PropTypes.func.isRequired,
  onPriceVisibilityChange: PropTypes.func.isRequired,
  vehicles: PropTypes.instanceOf(OrderedMap),
  addPrepurchaseLicensePlate: PropTypes.func.isRequired,
};

const defaultProps = {
  mounted: false,
  isSubmitted: false,
  selectedQuote: null,
  selectedLocation: null,
  venue: null,
  apiError: Map(),
  error: Map(),
  errorButton: false,
  fees: List(),
  submitButton: null,
  requiredFields: Set(),
  checkoutTotal: 0,
  subTotal: 0,
  couponAmount: null,
  userCreditAmount: null,
  couponCode: null,
  selectedAddOns: new AddOnsModel(),
  paymentMethods: OrderedMap(),
  requiredFieldErrors: Map(),
  applePayInstance: null,
  dataCollector: null,
  braintreeClientInstance: null,
  paypalInstance: null,
  vehicles: new OrderedMap(),
};

export class CheckoutForm extends Component {
  constructor(props) {
    super(props);

    this.onTimeInVisibilityChange = this.onTimeInVisibilityChange.bind(this);
    this.onTimeOutVisibilityChange = this.onTimeOutVisibilityChange.bind(this);
    this.renderPurchaseDeniedMessage = this.renderPurchaseDeniedMessage.bind(this);
  }

  onTimeInVisibilityChange(isVisible) {
    const { currentSearch, trackEvent } = this.props;
    if (isVisible) {
      trackEvent({
        ...TIME_IN_IMPRESSION,
        properties: {
          TimeSelected: currentSearch.startTime.format(),
          start_time: currentSearch.startTime.format(TIME_FORMAT),
        },
      });
    }
  }

  onTimeOutVisibilityChange(isVisible) {
    const { currentSearch, trackEvent } = this.props;
    if (isVisible) {
      trackEvent({
        ...TIME_OUT_IMPRESSION,
        properties: {
          TimeSelected: currentSearch.endTime.format(),
          end_time: currentSearch.endTime.format(TIME_FORMAT),
        },
      });
    }
  }

  get displayPaymentInformation() {
    return !(isNaN(this.props.checkoutTotal) || parseFloat(this.props.checkoutTotal) === 0);
  }

  renderPurchaseDeniedMessage() {
    if (!this.props.apiError.size) { return null; }
    const uuid = this.props.apiError.get('uuid');

    return (
      <div className="row margin-bottom-20">
        <div className="margin-right-15 margin-left-15 checkout-purchase-denied-message">
          <p><PurchaseDeniedMessage /></p>
          {uuid && <p>UUID: {uuid}</p>}
        </div>
      </div>
    );
  }

  render() {
    const {
      selectedQuote,
      selectedLocation,
      venue,
      error,
      locale,
      requestQueueLength,
      errorButton,
      fees,
      timezone,
      modifyRequiredFieldList,
      requiredFieldErrors,
      requiredFields,
      shouldScrollToError,
      setShouldScrollToError,
      submitCheckout,
      checkoutTotal,
      currentSearch,
      changeAddOns,
      selectedAddOns,
      user,
      routerLocation,
      userCreditAmount,
      paymentMethods,
      saveSearch,
      setUserInfo,
      trackEvent,
      applePayInstance,
      dataCollector,
      braintreeClientInstance,
      paypalInstance,
      userInfo,
      vehicles,
      addPrepurchaseLicensePlate,
      mounted,
    } = this.props;

    if (!selectedQuote || !selectedLocation) {
      return null;
    }

    const canSubmit = requestQueueLength === 0;

    const submitButtonClasses = cx({
      'btn': true,
      'btn-lg': true,
      'btn-block': true,
      'btn-book': true,
      'btn-primary': true,
      'error': errorButton,
      'progress-btn-background-green': true,
    });

    return (
      <div>
        <ParkingDetails
          selectedQuote={selectedQuote}
          selectedLocation={selectedLocation}
          timezone={timezone}
          onTimeInVisibilityChange={this.onTimeInVisibilityChange}
          onTimeOutVisibilityChange={this.onTimeOutVisibilityChange}
          onLocationDetailsVisibilityChange={this.props.onLocationDetailsVisibilityChange}
          error={error}
          saveSearch={saveSearch}
          locale={locale}
        />
        <TierAmenities
          quote={selectedQuote}
          venue={venue}
        />
        <AddOns
          addOns={selectedQuote.addOns}
          modifyRequiredFieldList={modifyRequiredFieldList}
          requiredFieldErrors={requiredFieldErrors}
          requiredFields={requiredFields}
          shouldScrollToError={shouldScrollToError}
          setShouldScrollToError={setShouldScrollToError}
          submitCheckout={submitCheckout}
          checkoutTotal={checkoutTotal}
          applePayInstance={applePayInstance}
          dataCollector={dataCollector}
          canSubmit={canSubmit}
          setUserInfo={setUserInfo}
          currentSearch={currentSearch}
          changeAddOns={changeAddOns}
          selectedAddOns={selectedAddOns}
          trackEvent={trackEvent}
          location={selectedLocation}
        />
        <div className="checkout-section">
          <VisibilitySensor onChange={this.props.onAccountVisibilityChange} partialVisibility>
            <AccountInformation
              {...this.props}
              user={user}
              userInfo={userInfo}
              onUserInfoChange={this.props.onUserInfoChange}
              modifyRequiredFieldList={modifyRequiredFieldList}
              requiredFieldErrors={requiredFieldErrors}
              requiredFields={requiredFields}
              shouldScrollToError={shouldScrollToError}
              setShouldScrollToError={setShouldScrollToError}
              submitCheckout={submitCheckout}
              checkoutTotal={checkoutTotal}
              applePayInstance={applePayInstance}
              dataCollector={dataCollector}
              canSubmit={canSubmit}
              setUserInfo={setUserInfo}
              trackEvent={trackEvent}
              routerLocation={routerLocation}
              location={selectedLocation}
              locale={locale}
              vehicles={vehicles}
              addPrepurchaseLicensePlate={addPrepurchaseLicensePlate}
              selectedQuote={selectedQuote}
              selectedLocation={selectedLocation}
            />
          </VisibilitySensor>
          <div>
            <PaymentInformation
              quote={selectedQuote}
              location={selectedLocation}
              submitButton={this.submitButton}
              braintreeClientInstance={braintreeClientInstance}
              paypalInstance={paypalInstance}
              dataCollector={dataCollector}
              submitCheckout={submitCheckout}
              checkoutTotal={checkoutTotal}
              applePayInstance={applePayInstance}
              setUserInfo={setUserInfo}
              trackEvent={trackEvent}
              locale={locale}
              display={this.displayPaymentInformation}
            />
          </div>
        </div>
        <div className="order-total-panel border-color-light-grey border-side-top border-style-solid padding-top-20 padding-bottom-40">
          <CheckoutSummary
            quote={selectedQuote}
            fees={fees}
            total={checkoutTotal}
            location={selectedLocation}
            addOns={selectedAddOns}
            userCreditAmount={userCreditAmount}
            currentSearch={currentSearch}
          />
        </div>
        <div className="purchase-panel">
          {this.renderPurchaseDeniedMessage()}
          <div className="purchase-button-container row margin-bottom-20">
            <div className="col-md-6 col-xs-12">
              <VisibilitySensor onChange={this.onCompletePurchaseVisibilityChange} partialVisibility>
                <ProgressButton
                  buttonClassName={submitButtonClasses}
                  id="checkout-purchase-btn"
                  canSubmit={canSubmit && mounted}
                  loading={this.props.isSubmitted}
                  onClick={this.props.handleSubmit}
                  ref={(btn) => { this.submitButton = btn; }}
                >
                  {
                    errorButton
                      ? (<span><ZagSad />ERROR!</span>)
                      : (<span>COMPLETE PURCHASE</span>)
                  }
                </ProgressButton>
              </VisibilitySensor>
            </div>
            <div className="col-md-6 col-xs-12">
              <ApplePayButton
                submitCheckout={submitCheckout}
                checkoutTotal={checkoutTotal}
                applePayInstance={applePayInstance}
                dataCollector={dataCollector}
                display={!!(paymentMethods.size)}
                canSubmit={canSubmit}
                modifyRequiredFieldList={modifyRequiredFieldList}
                setUserInfo={setUserInfo}
                trackEvent={trackEvent}
                locale={locale}
                location={selectedLocation}
                position="2"
              />
            </div>
          </div>
          <MinimalBadges cancellableStatus={selectedQuote.cancellableStatus} />
          <TermsOfService />
        </div>
      </div>
    );
  }
}

CheckoutForm.propTypes = propTypes;
CheckoutForm.defaultProps = defaultProps;

const CheckoutFormWrapper = props => (
  <AppConsumer><CheckoutForm {...props} /></AppConsumer>
);

export default CheckoutFormWrapper;
